/**
 * Active-build visual usage — the one portal projection shared by Assets and
 * Overview. It deliberately reports only manifest-confirmed relationships:
 * attached-library names are never promoted to “live” by a fuzzy match.
 */
import type { ManifestSummary, ManifestVisualUsage } from '@djgg/creations';

export function visualUsage(summary: ManifestSummary | undefined): ManifestVisualUsage[] {
  if (!summary) return [];
  if (summary.visualUsage) return summary.visualUsage;

  // Older build summaries predate `visualUsage`. Their base bindings remain
  // trustworthy, but they cannot prove animation-strip usage or semantic
  // relationships, so expose only the confirmed base facts.
  if (summary.activeVisuals) {
    return summary.activeVisuals.map((role) => ({
      key: role.key,
      label: role.label,
      renderMode: role.assetName ? 'asset' : 'procedural',
      assetName: role.assetName,
      animations: [],
      visible: true,
    }));
  }
  return [
    {
      key: 'background',
      label: 'Background',
      renderMode: summary.backgroundSprite ? 'asset' : 'procedural',
      assetName: summary.backgroundSprite,
      animations: [],
      visible: true,
    },
    ...Object.entries(summary.entities).map(([key, assetName]) => ({
      key: `entity:${key}`,
      label: key.replace(/[-_:]+/g, ' '),
      renderMode: 'asset' as const,
      assetName,
      animations: [],
      visible: true,
    })),
  ];
}

/** Asset name → every confirmed live role/state that uses it. */
export function rolesByAsset(roles: ManifestVisualUsage[]): Map<string, string[]> {
  const result = new Map<string, string[]>();
  const add = (assetName: string, description: string) => {
    const key = assetName.toLowerCase();
    result.set(key, [...(result.get(key) ?? []), description]);
  };
  for (const role of roles) {
    if (role.assetName) add(role.assetName, role.label);
    for (const animation of role.animations) add(animation.assetName, `${role.label} · ${animation.state}`);
  }
  return result;
}

/** Exact role match only. A missing mapping must remain honest in Overview. */
export function roleForDesignEntity(roles: ManifestVisualUsage[], entityName: string) {
  const key = entityName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return roles.find((role) => role.key === `entity:${key}` || role.key === key);
}
